import React, { useCallback, useEffect, useState } from "react";
import { GeneralObject } from "../../definitions/general";
import { getDeviceId } from "../../utils/auth";
import { isValidString } from "../../utils/string";
import AuthContext from "./AuthContext";

const useDeviceCredentials = () => {
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [deviceSecret, setDeviceSecret] = useState<string | null>(null);

  const generateNewDeviceCredentials = useCallback(() => {
    const newDeviceId = getDeviceId();
    const newDeviceSecret = window.btoa(Date.now() + newDeviceId);

    console.log("newDeviceId", newDeviceId, newDeviceSecret);
    localStorage.setItem(`zo-device-id`, newDeviceId);
    localStorage.setItem(`zo-device-secret`, newDeviceSecret);

    setDeviceId(newDeviceId);
    setDeviceSecret(newDeviceSecret);
  }, []);

  useEffect(() => {
    const initialDeviceId = localStorage.getItem(`zo-device-id`);
    const initialDeviceSecret = localStorage.getItem(`zo-device-secret`);

    if (isValidString(initialDeviceId) && isValidString(initialDeviceSecret)) {
      setDeviceId(initialDeviceId);
      setDeviceSecret(initialDeviceSecret);
    } else {
      generateNewDeviceCredentials();
    }
  }, [generateNewDeviceCredentials]);

  return { deviceId, deviceSecret, generateNewDeviceCredentials };
};

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<string | null>(null);
  const [isLoggedIn, setLoggedIn] = useState<boolean | null>(null);
  const [user, setUser] = useState<any>();

  const { deviceId, deviceSecret, generateNewDeviceCredentials } =
    useDeviceCredentials();

  useEffect(() => {
    if (isLoggedIn === false) {
      console.log("generating new device credentials", isLoggedIn);
      generateNewDeviceCredentials();
    }
  }, [generateNewDeviceCredentials, isLoggedIn]);

  useEffect(() => {
    const _token = localStorage.getItem("zo_world_token");
    if (token) {
      localStorage.setItem("zo_world_token", token);
    } else {
      if (_token) {
        setToken(_token);
      } else {
        setLoggedIn(false);
      }
    }
  }, [token]);

  useEffect(() => {
    const _tokenExpiry = localStorage.getItem("zo_world_token_expiry");
    if (tokenExpiry) {
      localStorage.setItem("zo_world_token_expiry", tokenExpiry);
    } else {
      if (_tokenExpiry) {
        setTokenExpiry(_tokenExpiry);
      } else {
        setLoggedIn(false);
      }
    }
  }, [tokenExpiry]);

  useEffect(() => {
    const localStorageUser = localStorage.getItem("zo_world_user");
    const localUser = localStorageUser ? JSON.parse(localStorageUser) : null;
    const _user = localUser;
    if (user) {
      localStorage.setItem("zo_world_user", JSON.stringify(user));
    } else {
      if (_user) {
        setUser(_user);
      } else {
        setLoggedIn(false);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user && token) {
      setLoggedIn(true);
    }
  }, [user, token]);

  const logout = useCallback(() => {
    localStorage.removeItem("zo_world_user");
    localStorage.removeItem("zo_world_token_expiry");
    localStorage.removeItem("zo_world_token");
    setLoggedIn(false);
    generateNewDeviceCredentials();
  }, [generateNewDeviceCredentials, setLoggedIn]);

  const login = (
    _token: string,
    _tokenExpiry: string,
    _user: GeneralObject
  ) => {
    setToken(_token);
    setTokenExpiry(_tokenExpiry);
    setUser(_user);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        isLoggedIn,
        user,
        logout,
        login,
        deviceSecret,
        deviceId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
